import { useCallback, useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, Loader2, CalendarRange, X, Plus, FileCode2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/context/LanguageContext";
import { parseFile } from "@/lib/api";
import { WIZARD } from "@/constants/testIds/wizard";

const MAX_PRIOR_YEARS = 4;

export default function UploadStep({ onParsed, sectors = [], sector = "generico", onSectorChange, periodMonths = 12, onPeriodMonthsChange }) {
  const { t, lang } = useLang();
  const inputRef = useRef(null);
  const priorInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [priorLoading, setPriorLoading] = useState(false);
  const [type, setType] = useState("ordinario");
  const [fileName, setFileName] = useState("");
  // priorYears: [{label, items, filename}]
  const [priorYears, setPriorYears] = useState([]);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const name = (file.name || "").toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xls") && !name.endsWith(".xbrl") && !name.endsWith(".xml")) {
      toast.error(t("err_file_type"));
      return;
    }
    setFileName(file.name);
    setLoading(true);
    try {
      const res = await parseFile(file);
      if (res.kind === "xbrl" && Array.isArray(res.periods) && res.periods.length > 1) {
        // Auto-split: latest period = current, others = prior years
        const [current, ...rest] = res.periods;
        const autoPrior = rest.slice(0, MAX_PRIOR_YEARS).map((p) => ({
          label: p.label,
          items: p.items,
          filename: `${file.name} · ${p.label}`,
        }));
        toast.success(t("xbrl_multi_period_title"));
        onParsed(current.items, autoPrior);
        return;
      }
      if (res.kind === "xbrl") {
        const p = res.periods?.[0];
        if (!p) throw new Error("Empty XBRL");
        onParsed(p.items, priorYears.map((py) => ({ label: py.label, items: py.items })));
        return;
      }
      // Excel
      onParsed(res.items, priorYears.map((py) => ({ label: py.label, items: py.items })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err_generic"));
    } finally {
      setLoading(false);
    }
  }, [onParsed, priorYears, t]);

  const handlePriorFile = async (file) => {
    if (!file) return;
    if (priorYears.length >= MAX_PRIOR_YEARS) {
      toast.error(`Max ${MAX_PRIOR_YEARS}`);
      return;
    }
    const name = (file.name || "").toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xls") && !name.endsWith(".xbrl") && !name.endsWith(".xml")) {
      toast.error(t("err_file_type"));
      return;
    }
    setPriorLoading(true);
    try {
      const res = await parseFile(file);
      const items = res.kind === "xbrl" ? (res.periods?.[0]?.items || []) : (res.items || []);
      const label = res.kind === "xbrl" ? (res.periods?.[0]?.label || "N-1") : `N-${priorYears.length + 1}`;
      setPriorYears((prev) => [...prev, { label, items, filename: file.name }]);
      toast.success(`${items.length} ${t("prior_uploaded")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err_generic"));
    } finally {
      setPriorLoading(false);
      if (priorInputRef.current) priorInputRef.current.value = "";
    }
  };

  const removePriorYear = (idx) => setPriorYears((prev) => prev.filter((_, i) => i !== idx));
  const updatePriorLabel = (idx, label) =>
    setPriorYears((prev) => prev.map((py, i) => (i === idx ? { ...py, label } : py)));

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">{t("upload_title")}</h1>
        <p className="mt-3 text-slate-600">{t("upload_subtitle")} · XBRL (.xbrl / .xml)</p>
      </div>

      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-3 text-sm font-medium text-slate-800">{t("upload_type_label")}</div>
        <RadioGroup value={type} onValueChange={setType} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Label htmlFor="type-ord" className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${type === "ordinario" ? "border-blue-500 bg-blue-50/60" : "border-slate-200 bg-white hover:border-slate-300"}`}>
            <RadioGroupItem value="ordinario" id="type-ord" data-testid={WIZARD.statementTypeOrd} className="mt-0.5" />
            <div><div className="font-medium text-slate-900">{t("upload_type_ord")}</div><div className="text-xs text-slate-500">art. 2424 / 2425 c.c.</div></div>
          </Label>
          <Label htmlFor="type-abb" className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${type === "abbreviato" ? "border-blue-500 bg-blue-50/60" : "border-slate-200 bg-white hover:border-slate-300"}`}>
            <RadioGroupItem value="abbreviato" id="type-abb" data-testid={WIZARD.statementTypeAbb} className="mt-0.5" />
            <div><div className="font-medium text-slate-900">{t("upload_type_abb")}</div><div className="text-xs text-slate-500">art. 2435-bis c.c.</div></div>
          </Label>
        </RadioGroup>

        <div className="mt-6 border-t border-slate-100 pt-5">
          <div className="mb-2 text-sm font-medium text-slate-800">{t("sector_label")}</div>
          <div className="text-xs text-slate-500 mb-3">{t("sector_hint")}</div>
          <Select value={sector} onValueChange={onSectorChange}>
            <SelectTrigger data-testid="wizard-sector-select" className="h-10 w-full bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {sectors.map((s) => (<SelectItem key={s.key} value={s.key}>{lang === "it" ? s.name_it : s.name_en}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-6 border-t border-slate-100 pt-5">
          <div className="mb-2 text-sm font-medium text-slate-800">{t("period_months_label")}</div>
          <div className="text-xs text-slate-500 mb-3">{t("period_months_hint")}</div>
          <Select value={String(periodMonths)} onValueChange={(v) => onPeriodMonthsChange?.(Number(v))}>
            <SelectTrigger data-testid="wizard-period-months-select" className="h-10 w-full bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="12" data-testid="period-12">{t("period_months_12")}</SelectItem>
              <SelectItem value="9" data-testid="period-9">{t("period_months_9")}</SelectItem>
              <SelectItem value="6" data-testid="period-6">{t("period_months_6")}</SelectItem>
              <SelectItem value="3" data-testid="period-3">{t("period_months_3")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div
        data-testid={WIZARD.dropZone}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        className={`relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-14 text-center transition-colors ${isDragging ? "border-blue-500 bg-blue-50" : "border-blue-300 bg-blue-50/40 hover:bg-blue-50"}`}
      >
        <div className="grid h-14 w-14 place-items-center rounded-full bg-white shadow-sm">
          <UploadCloud className="h-7 w-7 text-blue-700" strokeWidth={1.75} />
        </div>
        <div className="mt-5 font-heading text-lg font-medium text-slate-900">
          {t("upload_drop")}{" "}
          <button type="button" data-testid={WIZARD.browseBtn} onClick={() => inputRef.current?.click()} className="text-blue-700 underline-offset-2 hover:underline">
            {t("upload_browse")}
          </button>
        </div>
        <div className="mt-2 text-xs text-slate-500">.xlsx / .xls / .xbrl / .xml · {t("upload_hint")}</div>
        <input
          ref={inputRef}
          type="file"
          data-testid={WIZARD.fileInput}
          accept=".xlsx,.xls,.xbrl,.xml,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/xml,application/xml"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />

        {fileName && (
          <div className="mt-6 flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs text-slate-700">
            {fileName.toLowerCase().endsWith(".xbrl") || fileName.toLowerCase().endsWith(".xml") ? (
              <FileCode2 className="h-4 w-4 text-blue-600" strokeWidth={1.75} />
            ) : (
              <FileSpreadsheet className="h-4 w-4 text-green-600" strokeWidth={1.75} />
            )}
            {fileName}
          </div>
        )}

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-white/80 backdrop-blur-sm">
            <div className="flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm text-slate-800 shadow-sm ring-1 ring-slate-200">
              <Loader2 className="h-4 w-4 animate-spin text-blue-700" />
              {t("parsing")}
            </div>
          </div>
        )}
      </div>

      {/* Prior years (multi) */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="prior-upload-box">
        <div className="flex items-start gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-slate-100 text-slate-600">
            <CalendarRange className="h-4 w-4" strokeWidth={2} />
          </div>
          <div className="flex-1">
            <div className="font-heading text-sm font-semibold text-slate-900">{t("prior_upload_title")}</div>
            <div className="text-xs text-slate-500">{t("prior_upload_hint")}</div>
          </div>
        </div>

        {priorYears.length > 0 && (
          <div className="mt-4 space-y-2">
            {priorYears.map((py, idx) => (
              <div key={idx} data-testid={`prior-year-${idx}`} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2 pl-3">
                <FileSpreadsheet className="h-4 w-4 shrink-0 text-slate-500" strokeWidth={1.75} />
                <Input
                  data-testid={`prior-year-label-${idx}`}
                  value={py.label}
                  onChange={(e) => updatePriorLabel(idx, e.target.value)}
                  className="h-8 w-24 bg-white text-xs font-mono"
                />
                <span className="flex-1 truncate text-xs text-slate-600">{py.filename}</span>
                <span className="whitespace-nowrap rounded-full bg-white px-2 py-0.5 text-[11px] font-mono text-slate-700 ring-1 ring-slate-200">
                  {py.items.length} {t("prior_uploaded")}
                </span>
                <button
                  type="button"
                  data-testid={`prior-remove-${idx}`}
                  onClick={() => removePriorYear(idx)}
                  className="rounded-full p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                >
                  <X className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-slate-500">{priorYears.length} / {MAX_PRIOR_YEARS}</div>
          <Button
            variant="outline"
            size="sm"
            data-testid="prior-upload-btn"
            onClick={() => priorInputRef.current?.click()}
            disabled={priorLoading || priorYears.length >= MAX_PRIOR_YEARS}
            className="rounded-full border-slate-300"
          >
            {priorLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" strokeWidth={2} />}
            {t("prior_add")}
          </Button>
          <input
            ref={priorInputRef}
            type="file"
            data-testid="prior-file-input"
            accept=".xlsx,.xls,.xbrl,.xml,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/xml,application/xml"
            className="hidden"
            onChange={(e) => handlePriorFile(e.target.files?.[0])}
          />
        </div>
      </div>
    </div>
  );
}
